import { AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { ImeiValidation } from '../modules/mobilephone/models/trade-in-api.model';
import { TradeInHelperService } from '../modules/mobilephone/services/trade-in-helper.service';
import { EquipmentService } from '../services/equipment.service';
import { OrderService } from '../services/order.service';
import { SetIsValidatingImeiAction } from '../states/customer-product-mobilephone/customer-product-mobilephone.actions';
import { updateFormAfterAsyncValidation } from '@app/core/utils';
import { IdentifierType } from '../models/sps-order-item.model';

// http://docs.jquery.com/Plugins/Validation/Methods/creditcard
export function ImeiValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (/[^0-9-]+/.test(control.value)) {
      return null;
    }

    let nCheck = 0;
    let bEven = false;

    const newValue = control.value.replace(/\D/g, '');

    const noZeroImeiRegex = new RegExp('^0{15,}$');
    if (noZeroImeiRegex.test(newValue)) {
      return { imeiInvalid: true };
    }

    for (let n = newValue.length - 1; n >= 0; n--) {
      const cDigit = newValue.charAt(n);
      let nDigit = parseInt(cDigit, 10);

      if (bEven) {
        nDigit *= 2;
        if (nDigit > 9) {
          nDigit -= 9;
        }
      }
      nCheck += nDigit;
      bEven = !bEven;
    }

    return nCheck % 10 === 0 ? null : { imeiInvalid: true };
  };
}

export function DuplicateImeiInShoppingCartValidator(existingImeis: string[]): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    return !!existingImeis?.find(imei => imei === control.value) ? { imeiDuplicateInvalid: true } : null;
  };
}

export function ExistingImeiValidator(tradeInHelperService: TradeInHelperService): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const hasExistingError = tradeInHelperService.hasExistingError();
    const previouslyValidatedImei = tradeInHelperService.getPreviouslyValidatedImei();
    return hasExistingError && previouslyValidatedImei === control.value ? { imeiTradeInInvalid: true } : null;
  };
}

export function PreviouslyUsedImeiValidator(orderService: OrderService, store: Store): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors> => {
    if (!control.value) {
      return of(null);
    }

    store.dispatch(new SetIsValidatingImeiAction(true));

    return orderService.isIdentifierUsed(IdentifierType.IMEI, control.value).pipe(
      map((isImeiUsedBefore: boolean) => {
        store.dispatch(new SetIsValidatingImeiAction(false));
        return isImeiUsedBefore ? { isImeiUsedBefore: true } : null;
      }),
      catchError(() => {
        store.dispatch(new SetIsValidatingImeiAction(false));
        return of({ apiException: true });
      }),
      finalize(() => {
        updateFormAfterAsyncValidation(control);
      })
    );
  };
}

export function ImeiTradeInValidator(
  equipmentService: EquipmentService,
  tradeInHelperService: TradeInHelperService,
  onCompleteSuccessCallback: (result: ImeiValidation) => void = null,
  onCompleteFailureCallback: (error: string) => void = null
): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors> => {
    const previousImei = tradeInHelperService.getPreviouslyValidatedImei();
    if (!control.value || control.value === previousImei) {
      return of(null);
    }

    tradeInHelperService.setIsValidatingImei(true);
    return equipmentService.validateImeiTradeIn(control.value).pipe(
      map((result: ImeiValidation) => {
        tradeInHelperService.setIsValidatingImei(false);
        onCompleteSuccessCallback(result);
        return !!result.validateTradeInImeiError ? { invalidAsync: true } : null;
      }),
      catchError(() => {
        tradeInHelperService.setIsValidatingImei(false);
        onCompleteFailureCallback('An error occurred while validating IMEI');
        return of({ apiException: true });
      }),
      finalize(() => {
        updateFormAfterAsyncValidation(control);
      })
    );
  };
}
